import { DOCUMENT, ViewportScroller, NgClass, NgIf, NgFor } from '@angular/common';
import {
    Component,
    ElementRef, EventEmitter,
    HostBinding,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit, Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationStart, Router, RouterLink } from '@angular/router';
import { HomepageService } from '@app/_services/homepage.service';
import { PageCategoryService } from '@app/_services/page-category.service';
import { Category } from '@interfaces/category';
import { Product } from '@interfaces/product';
import { CartService } from '@services/cart.service';
import { RootService } from '@services/root.service';
import * as _ from 'lodash';
import { EMPTY, Subject, asyncScheduler, fromEvent, of } from 'rxjs';
import { catchError, debounceTime, filter, map, switchMap, takeUntil, throttleTime } from 'rxjs/operators';
export type SearchLocation = 'header' | 'indicator' | 'mobile-header' | 'page-header';

import { TranslateHelper } from '@app/_helpers';
import { SharedService } from '@app/_services/shared.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FiltersService } from '@services/filters.service';
import { CurrencyFormatPipe } from '../../pipes/currency-format.pipe';
import { MatTabsModule } from '@angular/material/tabs';
import { IconComponent } from '../icon/icon.component';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CurrencyConvertPipe } from '@app/shared/pipes';

export type CategoryWithDepth = Category & { depth: number };

@UntilDestroy()
@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    exportAs: 'search',
    providers: [TranslateHelper],
    standalone: true,
    imports: [NgClass, ExtendedModule, FormsModule, ReactiveFormsModule, NgIf, IconComponent, MatTabsModule, NgFor, RouterLink, TranslateModule, CurrencyFormatPipe, CurrencyConvertPipe]
})
export class SearchComponent implements OnChanges, OnInit, OnDestroy {
    private destroy$: Subject<void> = new Subject<void>();

    form: UntypedFormGroup;
    hasSuggestions = false;
    searchQuery = '';
    index;
    categories: any[] = [];
    suggestedProducts: any[] = [];
    suggestedProperties: any[] = [];
    suggestedCompanies: any[] = [];
    suggestedCategories: any[] = [];
    addedToCartProducts: Product[] = [];
    image = 'assets/images/nologo.png';
    @Input() location: SearchLocation;
    @Output() escape: EventEmitter<void> = new EventEmitter<void>();
    @Output() closeButtonClick: EventEmitter<void> = new EventEmitter<void>();
    @HostBinding('class.search') classSearch = true;
    @HostBinding('class.search--location--header') get classSearchLocationHeader(): boolean { return (this.location === 'header' || this.location === 'page-header'); }
    @HostBinding('class.search--location--indicator') get classSearchLocationIndicator(): boolean { return this.location === 'indicator'; }
    @HostBinding('class.search--location--mobile-header') get classSearchLocationMobileHeader(): boolean { return this.location === 'mobile-header'; }
    @HostBinding('class.search--has-suggestions') get classSearchHasSuggestions(): boolean { return this.hasSuggestions; }
    @HostBinding('class.search--suggestions-open') classSearchSuggestionsOpen = false;
    @ViewChild('input') inputElementRef: ElementRef;

    get element(): HTMLElement { return this.elementRef.nativeElement; }

    get inputElement(): HTMLElement { return this.inputElementRef.nativeElement; }

    @Input() inSearchPage = false;
    @Input() searchDropdown;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private fb: UntypedFormBuilder,
        private elementRef: ElementRef,
        private zone: NgZone,
        private cart: CartService,
        public root: RootService,
        private router: Router,
        private filtersService: FiltersService,
        private route: ActivatedRoute,
        private homePageService: HomepageService,
        private scroller: ViewportScroller,
        public translate: TranslateService,
        public translateHelper: TranslateHelper,
        private pageService: PageCategoryService,
        private shareService: SharedService
    ) {

    }

    sector;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.location && this.location === 'header') {
            // this.shop.getCategories(null, 1).pipe(
            //     takeUntil(this.destroy$),
            // ).subscribe(categories => this.categories = this.getCategoriesWithDepth(categories));


            // This is used for the categories near the search (now removed)
            // this.homePageService.getHomePage().subscribe((response: any) => {
            //     this.categories = response.categories;

            // })
        }
    }

    ngOnInit(): void {

        this.shareService.sectorSubject$.pipe(takeUntil(this.destroy$)).subscribe(sector => {
            this.sector = sector?.data || '';
        })

        this.form = this.fb.group({
            category: ['all'],
            q: [''],
        });

        this.router.events
            .pipe(untilDestroyed(this),filter(event => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                if (this.router.url.includes('search') && !event.url.includes('search')) {
                    this.form.get("q").reset();
                }
            });

        this.route.queryParams.pipe(
            takeUntil(this.destroy$)
        ).subscribe(
            (query) => {
                if ((this.router.url.includes('/search') || this.location == 'page-header') && query.query) {
                    this.form.get('q').setValue(query.query);
                    this.pageService.setQuery(query.query)
                }
            }
        );
        this.form.get('q').valueChanges.pipe(
            throttleTime(250, asyncScheduler, { leading: true, trailing: true }),
            map(query => query?.trim()),
            debounceTime(1000),
            switchMap(query => {
                if (query) {
                    this.pageService.setQuery(query);
                    //if (!this.router.url.includes('search')) {
                    return this.homePageService.getSearchResult({ search: query, ...this.location === 'page-header' ? { s: this.sector.seo.slug, searchType: 'p' } : {} }); // getting all products in search for now
                    //}
                }
                return of([]);
            }),
            catchError(err => {
                return EMPTY;
            }),
            takeUntil(this.destroy$),
        ).subscribe(response => {
            this.clearSuggested();
            this.hasSuggestions = (response.products?.length) || response.properties?.length || response.companies?.length ? true : false;
            if (response.products?.length > 0) {
                this.suggestedProducts = response.products;
                this.index = 0;
            }
            if (response.properties?.length > 0) {
                this.suggestedProperties = response?.properties;
                if (!this.index && this.index != 0) this.index = 1;
            }

            if (response.companies?.length > 0) {
                this.suggestedCompanies = response.companies;
                if (!this.index && this.index != 0) this.index = 2;
            }

            /* if (response.categories?.length > 0) {
                this.suggestedCategories = response.categories;
                if (!this.index && this.index != 0) this.index = 3;
            } */
        });

        this.zone.runOutsideAngular(() => {
            fromEvent(this.document, 'click').pipe(
                takeUntil(this.destroy$),
            ).subscribe(event => {
                const activeElement = this.document.activeElement;
                // If the inner element still has focus, ignore the click.
                if (activeElement && activeElement.closest('.search') === this.element) {
                    return;
                }
                // Close suggestion if click performed outside of component.
                if (event.target instanceof HTMLElement && this.element !== event.target.closest('.search')) {
                    this.zone.run(() => this.closeSuggestion());
                }
            });

            fromEvent(this.element, 'focusout').pipe(
                debounceTime(10),
                takeUntil(this.destroy$),
            ).subscribe(() => {
                if (this.document.activeElement === this.document.body) {
                    return;
                }
                // Close suggestions if the focus received an external element.
                if (this.document.activeElement && this.document.activeElement.closest('.search') !== this.element) {
                    this.zone.run(() => this.closeSuggestion());
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
    clearSuggested() {
        this.suggestedCategories = [];
        this.suggestedCompanies = [];
        this.suggestedProducts = [];
        this.suggestedProperties = [];
        this.index = null;
    }

    openSuggestion(): void {
        this.classSearchSuggestionsOpen = true;
    }

    closeSuggestion(): void {
        this.classSearchSuggestionsOpen = false;
    }

    getCategoryName(category: any): string {

        return '&nbsp;'.repeat(0) + category.name.en;
    }

    addToCart(product: Product): void {
        if (this.addedToCartProducts.includes(product)) {
            return;
        }

        this.addedToCartProducts.push(product);
        this.cart.add(product, 1).subscribe({
            complete: () => {
                this.addedToCartProducts = this.addedToCartProducts.filter(eachProduct => eachProduct !== product);
            }
        });
    }

    private getCategoriesWithDepth(categories: Category[], depth = 0): CategoryWithDepth[] {
        return categories.reduce<CategoryWithDepth[]>((acc, category) => [
            ...acc,
            { ...category, depth },
            ...this.getCategoriesWithDepth(category.children || [], depth + 1),
        ], []);
    }

    /**
    * @desc return image object key
    * @param {*} imageObject
    * @returns
    * @memberof SearchComponent
    */
    imagebObjectKey(imageObject) {
        return _.findKey(imageObject);
    }

    openSearchPage() {
        if (this.location === 'page-header') {
            return;
        }
        this.closeSuggestion();
        this.closeSearchDropdown();
        if (this.form.get('q').value && this.searchQuery != this.form.get('q').value) {
            this.searchQuery = this.form.get('q').value;
            this.router.navigate(
                ['/search'],
                {
                    queryParams: { q: this.form.get('q').value, searchType: 'p' /*  ,...this.location === 'page-header' ?  { s: this.sector.seo.slug } : {} */ },
                    queryParamsHandling: '',
                });
            if (this.router.url.includes('search')) {
                this.homePageService.getAdvancedSearchResult({ q: this.form.get('q').value, searchType: 'p' /* ,...this.location === 'page-header' ?  { s: this.sector.seo.slug } : {}  */ }).subscribe(data => {
                    this.filtersService.setFiltersEvent(data);
                });
            }
        }

    }

    closeSearchDropdown() {
        this.searchDropdown?.close();
    }

    scrollToTop() {
        this.scroller.scrollToPosition([0, 0]);
    }
}
